<template>
  <tr :class="rowClass">
    <!-- Họ tên -->
    <td class="align-middle">
      {{ homeWork.name || '-' }}
    </td>

    <!-- Buổi học -->
    <td class="align-middle">
      {{ lessonLabel }}
    </td>

    <!-- Phân loại -->
    <td class="align-middle">
      <span :class="attemptClass">
        {{ attemptLabel }}
      </span>
    </td>

    <!-- Mã học viên -->
    <td class="align-middle">
      {{ studentCode }}
    </td>

    <!-- Số điện thoại -->
    <td class="align-middle">
      {{ homeWork.phone || '-' }}
    </td>

    <!-- Trạng thái -->
    <td class="align-middle text-center">
      <el-button
          v-if="statusConfig"
          :type="statusConfig.type"
          size="small"
          class="status-button text-white w-100"
      >
        {{ statusConfig.label }}
      </el-button>

      <span v-else>
        -
      </span>
    </td>

    <!-- Hình thức chấm -->
    <td class="align-middle text-center">
      {{ markType }}
    </td>

    <!-- Deadline -->
    <td class="align-middle text-center">
      {{ deadlineText }}
    </td>

    <!-- Thời gian GV chấm -->
    <td class="align-middle text-center">
      <span v-if="markTime">
        {{ markTime | formatDateTimeVietnam }}
      </span>

      <span v-else>
        -
      </span>
    </td>

    <!-- Hành động -->
    <td class="align-middle text-center">
      <div
          v-if="canViewHomework"
          v-b-tooltip.hover
          title="Xem kết quả"
          class="btn btn-icon btn-xs btn-outline-success ml-1"
          @click="viewHomework"
      >
        <i class="far fa-eye" />
      </div>

      <div
          v-if="canMarkHomework"
          v-b-tooltip.hover
          :title="markActionTitle"
          class="btn btn-icon btn-xs btn-outline-info ml-1"
          @click="markHomework"
      >
        <i class="fas fa-pen-nib" />
      </div>

      <div
          v-if="canViewHistory"
          v-b-tooltip.hover
          title="Lịch sử chấm bài tập về nhà"
          class="btn btn-icon btn-xs btn-outline-info ml-1"
          @click="viewHistory"
      >
        <i class="fas fa-file-alt" />
      </div>
      <div @click="$emit('resetHomework', homeWork)" v-if="canResetMark" title="Reset kết quả"
        class="btn btn-icon btn-xs btn-outline-danger ml-2">
        <i class="fas fa-undo"></i>
      </div>
      <div
          v-if="canReopenHomework"
          v-b-tooltip.hover
          title="Làm lại bài tập về nhà"
          class="btn btn-icon btn-xs btn-outline-warning ml-1"
          @click="reopenHomework"
      >
        <i class="el-icon-refresh" />
      </div>

      <span v-if="!hasAction">
        -
      </span>
    </td>
  </tr>
</template>

<script>
const HOMEWORK_ATTEMPT = {
  FIRST: 'btvn_first',
  SECOND: 'btvn_second'
}

const SECOND_STATUS_COLOR = {
  1: 'primary',
  2: 'info',
  3: 'success',
  4: 'warning'
}

export default {
  name: 'HomeWork',

  props: {
    homeWork: {
      type: Object,
      required: true
    },

    learning_schedule_id: {
      type: [String, Number],
      default: null
    },

    lessonNumber: {
      type: [String, Number],
      default: null
    }
  },
  computed: {
    canResetMark() {
      return this.homeWork.test
        && this.homeWork.test.has_handmade_mark_test
        && this.homeWork.test.status_mark;
    },

    isFirstAttempt() {
      return (
          this.homeWork.homework_attempt ===
          HOMEWORK_ATTEMPT.FIRST
      )
    },

    isSecondAttempt() {
      return (
          this.homeWork.homework_attempt ===
          HOMEWORK_ATTEMPT.SECOND
      )
    },

    rowClass() {
      return {
        'homework-first-row': this.isFirstAttempt,
        'homework-second-row': this.isSecondAttempt
      }
    },

    attemptClass() {
      return {
        'attempt-first': this.isFirstAttempt,
        'attempt-second': this.isSecondAttempt
      }
    },

    attemptLabel() {
      if (this.homeWork.btvn_count) {
        return this.homeWork.btvn_count
      }

      if (this.isFirstAttempt) {
        return 'Lần 1'
      }

      if (this.isSecondAttempt) {
        return 'Lần 2'
      }

      return '-'
    },

    lessonLabel() {
      return this.homeWork.lesson || '-'
    },

    studentCode() {
      const userId = Number(this.homeWork.user_id)

      return Number.isNaN(userId)
          ? '-'
          : userId + 100000
    },

    test() {
      return this.homeWork.test || null
    },

    isMarked() {
      return this.toBoolean(
          this.test && this.test.status_mark
      )
    },

    isInTime() {
      return this.toBoolean(
          this.test && this.test.in_time
      )
    },

    hasHandmadeMark() {
      return this.toBoolean(
          this.test &&
          this.test.has_handmade_mark_test
      )
    },

    statusConfig() {
      if (this.isSecondAttempt) {
        return this.secondAttemptStatus
      }

      return this.firstAttemptStatus
    },

    firstAttemptStatus() {
      if (!this.test) {
        return {
          label: 'Chưa làm bài tập',
          type: 'warning'
        }
      }

      if (!this.isMarked && this.isInTime) {
        return {
          label: 'Chưa được chấm bài',
          type: 'primary'
        }
      }

      if (!this.isMarked && !this.isInTime) {
        return {
          label: 'Nộp bài muộn',
          type: 'danger'
        }
      }

      return {
        label: 'Đã chấm điểm',
        type: 'success'
      }
    },

    secondAttemptStatus() {
      const status = Number(this.homeWork.status)

      if (!status) {
        return null
      }

      return {
        label: this.homeWork.status_name || '-',
        type: SECOND_STATUS_COLOR[status] || 'info'
      }
    },

    markType() {
      if (!this.test) {
        return '-'
      }

      return this.hasHandmadeMark
          ? 'Chấm tự động & GV chấm'
          : 'Chấm tự động'
    },

    deadlineText() {
      if (!this.isSecondAttempt) {
        return '-'
      }

      if (Number(this.homeWork.status) === 2) {
        return '-'
      }

      return this.homeWork.deadline || '-'
    },

    markTime() {
      if (!this.test) {
        return null
      }

      if (this.isSecondAttempt) {
        return Number(this.homeWork.status) === 3
            ? this.test.updated_at
            : null
      }

      const homeworkGrader = this.toBoolean(
          this.test.homework_grader
      )

      if (
          this.hasHandmadeMark &&
          !homeworkGrader &&
          this.isMarked
      ) {
        return (
            this.test.mark_at ||
            this.test.updated_at ||
            null
        )
      }

      return null
    },

    canViewHomework() {
      return Boolean(this.test)
    },

    canMarkHomework() {
      if (!this.test) {
        return false
      }

      if (this.isSecondAttempt) {
        return Number(this.homeWork.status) === 1
      }

      return !this.isMarked
    },

    canViewHistory() {
      return this.toBoolean(
          this.homeWork.is_history
      )
    },

    canReopenHomework() {
      if (!this.isSecondAttempt) {
        return false
      }

      const homeworkType = Number(
          this.homeWork.type
      )

      const homeworkStatus = Number(
          this.homeWork.status
      )

      if (
          homeworkType === 0 ||
          homeworkType === 1
      ) {
        return true
      }

      return Boolean(
          this.test &&
          !this.hasHandmadeMark &&
          homeworkStatus === 3
      )
    },

    hasAction() {
      return Boolean(
          this.canViewHomework ||
          this.canMarkHomework ||
          this.canViewHistory ||
          this.canReopenHomework
      )
    },

    markActionTitle() {
      return this.isSecondAttempt
          ? 'Chấm lại bài tập'
          : 'Chấm bài tập'
    }
  },
  methods: {
    toBoolean(value) {
      return (
          value === true ||
          value === 1 ||
          value === '1'
      )
    },

    getCommonQuery() {
      return {
        classroom_id: this.homeWork.classroom_id,
        learning_schedule_id:
        this.learning_schedule_id,
        student_id: this.homeWork.user_id,
        lesson: this.lessonNumber,
        center_id: this.$route.query.center_id
      }
    },

    getViewRouteName() {
      return this.isSecondAttempt
          ? 'content-homework-v2'
          : 'content-homework'
    },

    getMarkRouteName() {
      return this.isSecondAttempt
          ? 'mark-homework-v2'
          : 'mark-homework'
    },

    viewHomework() {
      if (!this.test) {
        return
      }

      const routeData = this.$router.resolve({
        name: this.getViewRouteName(),

        params: {
          id: this.homeWork.classroom_id
        },

        query: {
          ...this.getCommonQuery(),
          type: 'view-result'
        }
      })

      window.open(routeData.href, '_blank')
    },

    markHomework() {
      if (!this.test) {
        return
      }

      const routeConfig = {
        name: this.getMarkRouteName(),

        params: {
          id: this.homeWork.classroom_id
        },

        query: {
          ...this.getCommonQuery(),
          overall_id: this.test.overall_id
        }
      }

      if (this.isSecondAttempt) {
        const routeData = this.$router.resolve(
            routeConfig
        )

        window.open(routeData.href, '_blank')

        return
      }

      this.$router.push({
        ...routeConfig,

        query: {
          ...routeConfig.query,
          type: 'mark-homework'
        }
      })
    },

    viewHistory() {
      const query = {
        classroom_id: this.homeWork.classroom_id,
        lesson: this.lessonNumber,
        course_student_id:
        this.homeWork.course_student_id,
        student_id: this.homeWork.user_id
      }

      if (this.isSecondAttempt) {
        query.type = 'redo'
      }

      const routeData = this.$router.resolve({
        name: 'history-homework',

        params: {
          id: this.homeWork.classroom_id
        },

        query
      })

      window.open(routeData.href, '_blank')
    },

    reopenHomework() {
      this.$emit(
          'reopen-homework',
          this.homeWork
      )
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/sass/custom/custom-teacher-edu.scss";

.status-button {
  min-height: 32px;
  padding: 8px 12px !important;
}

.btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.attempt-first {
  color: #409eff;
}

.attempt-second {
  color: #67c23a;
}

.homework-first-row {
  background-color: rgba(64, 158, 255, 0.02);
}

.homework-second-row {
  background-color: rgba(103, 194, 58, 0.02);
}
</style>
